import React, { useCallback, useEffect } from "react";

import { Drawer } from "@common/components/Drawer/Drawer";
import PageWrap from "@common/components/PageWrap/PageWrap";
import { BaseTable } from "@common/components/ShakeTable/BaseTable";
import { Spinner } from "@common/components/Spinner/Spinner";
import { PlatformName } from "@common/models";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { ActivityHistoryFilterView } from "@main/pages/shared/components/ActivityHistoryTable/SearchAndFilter/ActivityHistoryFilterView";
import { useActivityHistoryFilter } from "@main/pages/shared/consumers/useActivityHistoryFilter";
import useUserFeedbackAiSummaryConsumer from "@main/pages/UserFeedback/consumers/useUserFeedbackAiSummaryConsumer";

import { useActivityHistoryApiConsumer } from "../../consumers/useActivityHistoryApiConsumer";
import { resolveEmptyStateView } from "../../helpers/resolveEmptyStateView";
import { EmptyStateViewConf, EmptyTableView } from "../EmptyTableView/EmptyTableView";
import { LogDetails } from "./LogDetails/LogDetails";

interface Props {
  issueId?: string;
  ticketKey: string;
  type: "crash" | "user_feedback";
  hasLogs?: boolean;
}

export const ActivityHistoryTable = ({ issueId, ticketKey, type, hasLogs }: Props) => {
  const { selectedWorkspace, selectedApp } = useAppSelectionContext();

  let isWebApp: boolean | undefined = undefined;
  if (selectedApp) isWebApp = selectedApp.platform.name === PlatformName.WEB;

  const { filter, updateFilter } = useActivityHistoryFilter(isWebApp ?? false);

  const { getNextLog, getPreviousLog, items, loading, activityTableProps, detailLog, setDetailLog, error } =
    useActivityHistoryApiConsumer({
      type: type,
      teamId: selectedWorkspace?.id,
      appId: selectedApp?.id,
      ticketKey: ticketKey,
      hasLogs,
      filter,
      isWebApp,
    });

  const { response } = useUserFeedbackAiSummaryConsumer({
    selectedWorkspaceId: selectedWorkspace?.id,
    selectedAppId: selectedApp?.id,
    userFeedbackId: issueId,
    enabled: Boolean(selectedApp?.ai_enabled),
  });

  const handleNavigateToNext = useCallback(() => {
    if (!detailLog) {
      return;
    }
    const nextDetailLog = getNextLog(detailLog.id);
    nextDetailLog && setDetailLog(nextDetailLog);
    // eslint-disable-next-line
  }, [getNextLog, detailLog]);

  const handleNavigateToPrevious = useCallback(() => {
    if (!detailLog) {
      return;
    }
    const previousDetailLog = getPreviousLog(detailLog.id);
    previousDetailLog && setDetailLog(previousDetailLog);
  }, [getPreviousLog, setDetailLog, detailLog]);

  const handleLogClose = useCallback(() => {
    setDetailLog(undefined);
    activityTableProps.table.resetRowSelection();
    // eslint-disable-next-line
  }, [setDetailLog]);

  useEffect(() => {
    if (detailLog) {
      const selectedRow = activityTableProps.table.getSelectedRowModel().rows?.[0];
      if (selectedRow?.original?.id === detailLog?.id) return;

      activityTableProps.table.resetRowSelection();
      const row = activityTableProps.table.getRowModel().rows.find((a) => a.original.id === detailLog?.id);
      if (row) {
        row?.toggleSelected(true);
      } else {
        setDetailLog(undefined);
      }
    }
    // eslint-disable-next-line
  }, [detailLog, filter]);

  useEffect(() => {
    if (filter.ai && response && response?.log_ids?.length > 0) {
      activityTableProps.setFlaggedRows(response.log_ids);
    } else {
      activityTableProps.setFlaggedRows([]); // clear flags
    }
    // eslint-disable-next-line
  }, [filter, response]);

  const hasItems = Boolean(items && items.length > 0);
  const emptyViewConfig = resolveEmptyStateView(loading, "logs", hasItems, selectedApp, true, error) as
    | EmptyStateViewConf
    | undefined;

  const isInitialLoading = typeof items == "undefined" && loading;

  return (
    <>
      <PageWrap style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <ActivityHistoryFilterView
          filter={filter}
          setFilter={updateFilter}
          showSheldonButton={Boolean(selectedApp?.ai_enabled)}
        />
        {emptyViewConfig && (
          <EmptyTableView
            withFilters
            {...emptyViewConfig}
          />
        )}
        {isInitialLoading && <Spinner />}

        {!emptyViewConfig && !isInitialLoading && <BaseTable {...activityTableProps} />}
      </PageWrap>
      <Drawer open={!!detailLog}>
        {detailLog && (
          <LogDetails
            log={detailLog}
            navigateToNext={handleNavigateToNext}
            navigateToPrevious={handleNavigateToPrevious}
            closeDrawer={handleLogClose}
            aiLogs={response?.log_ids}
          />
        )}
      </Drawer>
    </>
  );
};
